import { MenuItem, styled, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import { useAuth } from '@schooly/components/authentication';
import { TIMETABLE } from '@schooly/constants/test-ids';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import { IS_DEMO } from '../../../config';
import {
  allowRenderSchoolFrequencies,
  allowRenderSchoolLegalEntities,
  allowRenderSchoolProducts,
  allowRenderSchoolSettings,
} from '../../../helpers/renderRoutesByPermissions';
import { useTimetablePermissions } from '../../../hooks/useTimetablePermissions';
import { SidebarMenuList } from './Sidebar.styled';

export const SchoolMenu: FC = () => {
  return (
    <SidebarMenuList>
      <SchoolMenuItems />
    </SidebarMenuList>
  );
};

const SchoolMenuItems: FC = () => {
  const { pathname } = useLocation();
  const { permissions } = useAuth();
  const { canManageTimetables } = useTimetablePermissions();

  const showTimetables = canManageTimetables;
  const showSchoolSettings = allowRenderSchoolSettings(permissions);
  const showStructureSection = showSchoolSettings || showTimetables;
  const showSchoolProducts = allowRenderSchoolProducts(permissions);
  const showSchoolLegalEntities = allowRenderSchoolLegalEntities(permissions);
  const showSchoolFrequencies = allowRenderSchoolFrequencies(permissions);
  const showInvoicingSection =
    showSchoolProducts || showSchoolLegalEntities || showSchoolFrequencies;

  return (
    <>
      {showSchoolSettings && (
        <>
          <Link to="/settings" state={{ replace: true }}>
            <MenuItem selected={pathname === '/settings'}>
              <FormattedMessage id="school-sections-General" />
            </MenuItem>
          </Link>

          <SectionTitle variant="h4">
            <FormattedMessage id="section-Enrolment" />
          </SectionTitle>

          {!IS_DEMO && (
            <Link to="/settings/applications" state={{ replace: true }}>
              <MenuItem selected={pathname.startsWith('/settings/applications')}>
                <FormattedMessage id="school-sections-Applications" />
              </MenuItem>
            </Link>
          )}

          <Link
            to="/settings/rollover_and_leaving"
            state={{ replace: true }}
            onClick={() =>
              trackAmplitudeEvent({
                name: AnalyticsEventName.RLOpen,
                payload: { user_role: permissions },
              })
            }
          >
            <MenuItem selected={pathname.startsWith('/settings/rollover_and_leaving')}>
              <FormattedMessage id="section-RolloverAndLeaving" />
            </MenuItem>
          </Link>
        </>
      )}

      {showStructureSection && (
        <>
          <SectionTitle variant="h4">
            <FormattedMessage id="section-Structure" />
          </SectionTitle>

          {showSchoolSettings && (
            <Link to="/settings/school_periods" state={{ replace: true }}>
              <MenuItem selected={pathname.startsWith('/settings/school_periods')}>
                <FormattedMessage id="school-sections-SchoolPeriods" />
              </MenuItem>
            </Link>
          )}
          {showTimetables && (
            <>
              <Link to="/settings/timetable_structures" state={{ replace: true }}>
                <MenuItem selected={pathname.startsWith('/settings/timetable_structures')}>
                  <FormattedMessage id="school-sections-TimetableStructure" />
                </MenuItem>
              </Link>
              <Link
                to="/settings/timetables"
                state={{ replace: true }}
                data-test-id={TIMETABLE.MENU_LINK}
              >
                <MenuItem selected={pathname.startsWith('/settings/timetables')}>
                  <FormattedMessage id="school-sections-Timetable" />
                </MenuItem>
              </Link>
            </>
          )}
        </>
      )}

      {showSchoolSettings && (
        <>
          <SectionTitle variant="h4">
            <FormattedMessage id="section-Activity" />
          </SectionTitle>
          <Link to="/settings/assessments" state={{ replace: true }}>
            <MenuItem selected={pathname.startsWith('/settings/assessments')}>
              <FormattedMessage id="school-sections-Assessments" />
            </MenuItem>
          </Link>
          <Link to="/settings/attendance" state={{ replace: true }}>
            <MenuItem selected={pathname.startsWith('/settings/attendance')}>
              <FormattedMessage id="school-sections-Attendance" />
            </MenuItem>
          </Link>
          <Link to="/settings/conduct" state={{ replace: true }}>
            <MenuItem selected={pathname.startsWith('/settings/conduct')}>
              <FormattedMessage id="school-sections-Conduct" />
            </MenuItem>
          </Link>
        </>
      )}

      {showInvoicingSection && (
        <>
          <SectionTitle variant="h4">
            <FormattedMessage id="section-Invoicing" />
          </SectionTitle>

          {showSchoolLegalEntities && (
            <Link to="/settings/legal_entities" state={{ replace: true }}>
              <MenuItem selected={pathname.startsWith('/settings/legal_entities')}>
                <FormattedMessage id="section-LegalEntitiesAndIntegrations" />
              </MenuItem>
            </Link>
          )}
          {showSchoolFrequencies && (
            <Link to="/settings/frequencies" state={{ replace: true }}>
              <MenuItem selected={pathname.startsWith('/settings/frequencies')}>
                <FormattedMessage id="section-FrequenciesAndDates" />
              </MenuItem>
            </Link>
          )}
          {showSchoolProducts && (
            <Link to="/settings/products" state={{ replace: true }}>
              <MenuItem selected={pathname.startsWith('/settings/products')}>
                <FormattedMessage id="products-Title" />
              </MenuItem>
            </Link>
          )}
        </>
      )}

      {showSchoolSettings && (
        <>
          <SectionTitle variant="h4">
            <FormattedMessage id="section-Others" />
          </SectionTitle>

          <Link to="/settings/filters" state={{ replace: true }}>
            <MenuItem selected={pathname.startsWith('/settings/filters')}>
              <FormattedMessage id="school-sections-Filters" />
            </MenuItem>
          </Link>
          <Link to="/settings/custom_fields" state={{ replace: true }}>
            <MenuItem selected={pathname.startsWith('/settings/custom_fields')}>
              <FormattedMessage id="school-sections-CustomFields" />
            </MenuItem>
          </Link>
          <Link to="/settings/notifications" state={{ replace: true }}>
            <MenuItem selected={pathname.startsWith('/settings/notifications')}>
              <FormattedMessage id="school-sections-Notifications" />
            </MenuItem>
          </Link>
        </>
      )}
    </>
  );
};

const SectionTitle = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(3, 0, 0.5, 1),
  '&:first-child': {
    marginTop: theme.spacing(0),
  },
}));
